import { useEffect, useRef } from 'react';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';

import type { InsightScreenshotComment } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/insight-screenshot';

interface CommentsPanelProps {
  comments?: InsightScreenshotComment[];
  loading: boolean;
  selectedCommentId: string | number | null;
  onCommentClick: (commentId: string | number) => void;
}

const CommentsPanel = ({
  comments = [],
  loading,
  selectedCommentId,
  onCommentClick,
}: CommentsPanelProps): React.JSX.Element => {
  const selectedCommentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedCommentId && selectedCommentRef.current) {
      selectedCommentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedCommentId]);
  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 text-navy-800">Insights</h3>
        <Loader loading={true} />
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Insights</h3>
        <div className="text-center py-8">
          <p className="text-navy-800">No insights yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-navy-100 h-full">
      <div className="px-4 py-3">
        <h3 className="font-semibold text-navy-800">
          Insights ({comments.length})
        </h3>
      </div>

      <div className="max-h-[500px] overflow-y-auto custom-scroll mr-1 px-4 py-2.5 flex flex-col gap-2">
        {comments.map((comment, index) => {
          const isSelected = selectedCommentId === comment.id;
          const commentNumber = index + 1;

          return (
            <button
              key={comment.id ?? `comment-${commentNumber}`}
              ref={isSelected ? selectedCommentRef : null}
              className={classNames(
                'w-full text-left px-3 py-2.5 border border-navy-200 rounded-md cursor-pointer transition-all duration-200',
                {
                  'bg-indigo-05 shadow !border-navy-500': isSelected,
                  'hover:bg-gray-50': !isSelected,
                },
              )}
              onClick={() => {
                if (comment.id !== undefined) {
                  onCommentClick(comment.id);
                }
              }}
              type="button"
            >
              <div className="flex items-start gap-1.5">
                <div
                  className={classNames(
                    'size-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm',
                    {
                      'bg-navy-800': isSelected,
                      'bg-navy-500': !isSelected,
                    },
                  )}
                >
                  {commentNumber}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 text-sm break-words">
                    {comment.message}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CommentsPanel;
